// Préparation des brouillons : réponse, réponse à tous, transfert, nettoyage du texte saisi.
import DOMPurify from "dompurify";
import { parseAddressList } from "../src/headers";
import { replyAlias } from "./aliases";
import { api, type Draft, type DraftFields, type Mailbox, type Message, type SendingAlias } from "./api";
import { longDate, who } from "./format";
import { toBase64, type Decoded } from "./mime";

export const DOMAIN = "robinluiz.fr";
const isOwn = (address: string) => address.endsWith(`@${DOMAIN}`);

// Balises permises dans l'éditeur : la mise en forme simple choisie pour la v2.
const EDITOR_TAGS = ["p", "div", "br", "b", "strong", "i", "em", "u", "a", "ul", "ol", "li", "blockquote", "span"];

export function cleanEditorHtml(html: string): string {
  // data-signature repère la signature, pour la changer avec l'adresse d'expédition.
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: EDITOR_TAGS, ALLOWED_ATTR: ["href", "data-signature"], ALLOWED_URI_REGEXP: /^(https?:|mailto:)/i });
}

const escape = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

// Signature en fin de message, séparée par « -- » (convention des messageries).
export const signatureHtml = (text: string) => `<div data-signature="1">-- <br>${text.split("\n").map(escape).join("<br>")}</div>`;

// « Alice <a@b>, c@d ; e@f » → liste d'adresses. Les points-virgules (Outlook) sont acceptés.
export function parseRecipients(text: string): Mailbox[] {
  return parseAddressList(text.replace(/;/g, ","));
}

export function formatRecipients(list: Mailbox[]): string {
  return list.map((m) => (m.name ? `${m.name} <${m.address}>` : m.address)).join(", ");
}

// Contenu de l'e-mail d'origine à citer : le corps HTML seul, sans <head> ni <style>, qui
// ne doivent pas s'appliquer à l'application ni au message. Les images intégrées (data:)
// sont retirées au-delà de 1 Mo, pour tenir dans une ligne de la base (2 Mo).
function originalBody(decoded: Decoded): string {
  if (!decoded.html) return `<div style="white-space: pre-wrap">${escape(decoded.text)}</div>`;
  const doc = new DOMParser().parseFromString(decoded.html, "text/html");
  doc.querySelectorAll("style, script, head, meta, link, title").forEach((n) => n.remove());
  let body = doc.body?.innerHTML ?? "";
  if (body.length > 1_000_000) body = body.replace(/<img[^>]+src=["']?data:[^>]*>/gi, "[image]");
  return body;
}

const quoteStyle = "margin:0 0 0 .8ex; border-left:2px solid #ccc; padding-left:1ex";

function quoteText(text: string): string {
  return text.split(/\r?\n/).map((line) => `> ${line}`).join("\n");
}

function prefixed(subject: string | null, prefix: "Re" | "Fwd"): string {
  const s = subject ?? "";
  const already = prefix === "Re" ? /^re\s*:/i : /^(fwd?|tr)\s*:/i;
  return already.test(s) ? s : `${prefix}: ${s}`.trim();
}

export function replyFields(message: Message, decoded: Decoded, all: boolean, aliases: SendingAlias[] = []): DraftFields {
  const author = decoded.replyTo.length ? decoded.replyTo : [message.from];
  const others = all ? [...message.to, ...message.cc] : [];
  const seen = new Set<string>();
  const unique = (list: Mailbox[]) => list.filter((m) => {
    if (isOwn(m.address) || aliases.some((a) => a.alias === m.address) || seen.has(m.address)) return false;
    seen.add(m.address);
    return true;
  });
  // Réponse à un e-mail envoyé depuis la messagerie : on écrit de nouveau à ses destinataires.
  const to = unique(message.location === "sent" ? message.to : author);
  const cc = unique(others);

  const ids = [decoded.references, decoded.messageId].filter(Boolean).join(" ").trim();
  const intro = `Le ${longDate(message.receivedAt)}, ${who(message.from)} a écrit :`;
  const from = replyAlias(message, aliases);
  return {
    kind: "reply",
    ...(from ? { from } : {}),
    to, cc,
    subject: prefixed(message.subject, "Re"),
    inReplyTo: decoded.messageId,
    references: ids || null,
    threadId: message.threadId,
    quoteHtml: `<br><div>${escape(intro)}</div><blockquote style="${quoteStyle}">${originalBody(decoded)}</blockquote>`,
    quoteText: `${intro}\n${quoteText(decoded.text)}`,
  };
}

export function forwardFields(message: Message, decoded: Decoded): DraftFields {
  const lines = [
    "---------- Message transféré ----------",
    `De : ${message.from.name ? `${message.from.name} <${message.from.address}>` : message.from.address}`,
    `Date : ${longDate(message.receivedAt)}`,
    `Objet : ${message.subject ?? ""}`,
    `À : ${formatRecipients(message.to)}`,
    ...(message.cc.length ? [`Cc : ${formatRecipients(message.cc)}`] : []),
  ];
  return {
    kind: "forward",
    subject: prefixed(message.subject, "Fwd"),
    threadId: message.threadId,
    quoteHtml: `<br><div>${lines.map(escape).join("<br>")}</div><br>${originalBody(decoded)}`,
    quoteText: `${lines.join("\n")}\n\n${decoded.text}`,
  };
}

// Pièce jointe : le navigateur l'encode, le Worker la range telle quelle.
export async function attach(draftId: string, blob: Blob, name: string) {
  return api.addAttachment(draftId, name, blob.type, blob.size, toBase64(await blob.arrayBuffer()));
}

// Crée le brouillon d'un transfert, avec les pièces jointes de l'e-mail d'origine.
export async function createForward(message: Message, decoded: Decoded): Promise<Draft> {
  const draft = await api.createDraft(forwardFields(message, decoded));
  for (const a of decoded.attachments) {
    // Contenu lu directement : la politique de sécurité interdit fetch() sur une adresse blob:.
    await attach(draft.id, new Blob([a.blob], { type: a.mimeType }), a.filename);
  }
  return draft;
}
